import { useQuery } from '@tanstack/react-query';
import { navigationApi } from '@/api';
import { useAuthStore } from '@/store/authStore';
import type { NavigationResponse } from '@/types';

export const navigationKeys = {
  all: ['navigation'] as const,
  /** Scoped by user: two people on one machine must not share a cached menu. */
  current: (userId: number | undefined) => ['navigation', 'current', userId] as const,
};

/**
 * The menu for the signed-in user.
 *
 * Fetched once per session and cached: the sidebar is on screen constantly and
 * the answer only changes when an administrator edits a role. `queryClient` is
 * cleared on sign-out, so the next user never inherits the previous one's menu.
 */
export function useNavigation() {
  const userId = useAuthStore((state) => state.user?.id);

  return useQuery<NavigationResponse>({
    queryKey: navigationKeys.current(userId),
    queryFn: navigationApi.current,
    enabled: Boolean(userId),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    // A failed menu fetch leaves the user with no navigation at all, so retry
    // harder than the global default before giving up.
    retry: 2,
  });
}
